import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Lock } from "lucide-react";
import { api, fmtDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { MissingDataBadge, RedAlertBanner, Completeness, SeverityBadge } from "@/components/StatusBadge";
import { ProfileTab } from "@/components/patient/ProfileTab";
import { TrendsTab } from "@/components/patient/TrendsTab";
import { ConsentPanel } from "@/components/patient/ConsentPanel";
import { IntakeHub } from "@/components/intake/IntakeHub";
import { CasesTab } from "@/components/patient/CasesTab";
import { RemindersPanel } from "@/components/patient/RemindersPanel";
import { CascadePanel } from "@/components/patient/CascadePanel";
import { TestsTab } from "@/components/marketplace/TestsTab";

export default function PatientDetailPage() {
  const { id } = useParams();
  const { user } = useAuth();
  const [p, setP] = useState(null);
  const [err, setErr] = useState(null);
  const load = useCallback(() => api.get(`/patients/${id}`).then((r) => setP(r.data)).catch((e) => setErr(e?.response?.data?.detail || "Access denied")), [id]);
  useEffect(() => { load(); }, [load]);

  if (err) return <div data-testid="patient-access-denied" className="clinical-card p-8 flex gap-3 text-rose-800"><Lock className="w-5 h-5" /> {String(err)}</div>;
  if (!p) return <div className="text-sm text-slate-500">Loading…</div>;

  const role = user.role;
  const canIntake = ["admin", "doctor", "staff", "lab"].includes(role);
  const canConsent = ["admin", "doctor", "staff", "patient"].includes(role);
  const canCases = ["admin", "doctor", "staff", "patient", "nutritionist", "insurance"].includes(role);
  const canTrends = ["admin", "doctor", "staff", "patient", "nutritionist"].includes(role);
  const canFollow = ["admin", "doctor", "staff", "patient"].includes(role);
  const canTests = ["admin", "doctor", "staff", "patient"].includes(role);
  const ev = p.evaluation;
  const cascadeCount = ev?.cascade_recommendations?.length || 0;
  const missingReq = ev?.missing_data?.filter((m) => m.required).length || 0;
  const defaultTab = role === "lab" ? "intake" : role === "patient" ? "consent" : role === "nutritionist" || role === "insurance" ? "cases" : "profile";

  return (
    <div className="space-y-6" data-testid="patient-profile-card">
      <div className="clinical-card p-6 sm:p-8 flex flex-wrap items-center gap-6" data-testid="patient-demographics-section">
        <span className="w-20 h-20 rounded-2xl bg-teal-700 text-white grid place-items-center text-3xl font-heading font-bold">{p.name?.[0]}</span>
        <div className="flex-1 min-w-[220px]">
          <h1 className="font-heading text-2xl sm:text-3xl font-extrabold text-slate-900">{p.name}</h1>
          <p className="text-sm text-slate-600 mt-1">{p.sex || "—"} · DOB {fmtDate(p.dob)}{ev?.age != null && ` (${ev.age} y)`} · ABHA <span className="font-mono text-teal-800">{p.abha_id || "—"}</span></p>
          {p.scope_note && <p className="text-xs text-amber-800 mt-2 flex items-center gap-1"><Lock className="w-3 h-3" /> {p.scope_note}</p>}
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          {ev && <Completeness value={ev.completeness} />}
          <MissingDataBadge count={missingReq} />
          {ev?.red_alerts?.length > 0 && <SeverityBadge severity="red" />}
        </div>
      </div>
      {ev && role !== "patient" && <RedAlertBanner alerts={ev.red_alerts} testId="patient-red-alerts-banner" />}
      <Tabs defaultValue={defaultTab} className="space-y-4">
        <TabsList className="bg-white border border-slate-200 rounded-full p-1 h-auto flex-wrap">
          {!p.scoped && <TabsTrigger data-testid="tab-profile" value="profile" className="rounded-full data-[state=active]:bg-teal-700 data-[state=active]:text-white">Profile</TabsTrigger>}
          {canTrends && <TabsTrigger data-testid="tab-trends" value="trends" className="rounded-full data-[state=active]:bg-teal-700 data-[state=active]:text-white">Health trends</TabsTrigger>}
          {canIntake && <TabsTrigger data-testid="tab-intake" value="intake" className="rounded-full data-[state=active]:bg-teal-700 data-[state=active]:text-white">Data intake</TabsTrigger>}
          {canConsent && <TabsTrigger data-testid="tab-consent" value="consent" className="rounded-full data-[state=active]:bg-teal-700 data-[state=active]:text-white">Consent</TabsTrigger>}
          {canCases && <TabsTrigger data-testid="tab-cases" value="cases" className="rounded-full data-[state=active]:bg-teal-700 data-[state=active]:text-white">Cases & reports</TabsTrigger>}
          {canFollow && <TabsTrigger data-testid="tab-reminders" value="reminders" className="rounded-full data-[state=active]:bg-teal-700 data-[state=active]:text-white">Follow-ups</TabsTrigger>}
          {canFollow && <TabsTrigger data-testid="tab-cascade" value="cascade" className="rounded-full data-[state=active]:bg-teal-700 data-[state=active]:text-white">Family screening{cascadeCount > 0 && role !== "patient" && <span className="ml-1.5 w-2 h-2 rounded-full bg-red-600 inline-block pulse-red" />}</TabsTrigger>}
          {canTests && <TabsTrigger data-testid="tab-tests" value="tests" className="rounded-full data-[state=active]:bg-teal-700 data-[state=active]:text-white">Tests & devices</TabsTrigger>}
        </TabsList>
        {!p.scoped && <TabsContent value="profile"><ProfileTab patient={p} onSaved={load} /></TabsContent>}
        {canTrends && <TabsContent value="trends"><TrendsTab patient={p} /></TabsContent>}
        {canIntake && <TabsContent value="intake"><IntakeHub patient={p} onChanged={load} /></TabsContent>}
        {canConsent && <TabsContent value="consent"><ConsentPanel patient={p} onChanged={load} /></TabsContent>}
        {canCases && <TabsContent value="cases"><CasesTab patient={p} /></TabsContent>}
        {canFollow && <TabsContent value="reminders"><RemindersPanel patient={p} /></TabsContent>}
        {canFollow && <TabsContent value="cascade"><CascadePanel patient={p} /></TabsContent>}
        {canTests && <TabsContent value="tests"><TestsTab patient={p} /></TabsContent>}
      </Tabs>
    </div>
  );
}
